import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Flex,
  Heading,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import {
  resetPasswordApi,
  validateResetTokenApi,
} from "../../api/auth";
import FormInputPassword from "../../components/form/FormInputPassword";
import LayoutView from "../../components/layout";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const tokenQuery = useQuery(
    ["validateResetToken", token],
    () => validateResetTokenApi(token),
    { enabled: Boolean(token), retry: false },
  );

  const resetPasswordMutation = useMutation(resetPasswordApi, {
    onSuccess: (response) => {
      setError("");
      setSuccess(response.data?.msg);
    },
    onError: (requestError) => {
      setError(
        requestError.response?.data?.msg ||
          "Unable to reset your password. Please request a new link.",
      );
    },
  });

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must contain at least 8 characters")
        .required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords do not match")
        .required("Required"),
    }),
    onSubmit: (values) => {
      setError("");
      resetPasswordMutation.mutate({ token, ...values });
    },
  });

  const invalidToken = !token || tokenQuery.isError;

  return (
    <LayoutView>
      <Flex align="center" justifyContent="center" minH="60vh">
        <Box
          p={8}
          maxWidth="600px"
          w="full"
          borderWidth={1}
          borderRadius={8}
          boxShadow="lg"
        >
          {tokenQuery.isLoading ? (
            <Center minH="200px">
              <Spinner />
            </Center>
          ) : invalidToken ? (
            <Box textAlign="center">
              <Heading size="md">Reset link unavailable</Heading>
              <Text mt="4" color="red.500">
                {tokenQuery.error?.response?.data?.msg ||
                  "This password reset link is invalid or has expired."}
              </Text>
              <Button
                mt="6"
                variant="outline"
                onClick={() => navigate("/auth/forgot-password")}
              >
                Request a new link
              </Button>
            </Box>
          ) : success ? (
            <Box textAlign="center">
              <Heading size="md">Password reset complete</Heading>
              <Text mt="4" color="green.600">
                {success}
              </Text>
              <Button
                mt="6"
                width="full"
                variant="outline"
                onClick={() => navigate("/auth/login")}
              >
                Sign in
              </Button>
            </Box>
          ) : (
            <>
              <Heading textAlign="center" size="lg">
                Create a new password
              </Heading>
              <Text mt="3" textAlign="center" color="gray.600">
                Use at least 8 characters.
              </Text>
              <form onSubmit={formik.handleSubmit}>
                <FormInputPassword
                  mt="6"
                  label="New password"
                  id="password"
                  placeholder="********"
                  onChange={formik.handleChange("password")}
                  value={formik.values.password}
                  error={formik.touched.password && formik.errors.password}
                  onBlur={formik.handleBlur}
                />
                <FormInputPassword
                  mt="5"
                  label="Confirm new password"
                  id="confirmPassword"
                  placeholder="********"
                  onChange={formik.handleChange("confirmPassword")}
                  value={formik.values.confirmPassword}
                  error={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  onBlur={formik.handleBlur}
                />
                <Text color="red.500" textAlign="center" mt="2">
                  {error}
                </Text>
                <Button
                  variant="outline"
                  width="full"
                  mt="6"
                  type="submit"
                  isDisabled={resetPasswordMutation.isLoading}
                >
                  {resetPasswordMutation.isLoading ? (
                    <CircularProgress
                      isIndeterminate
                      size="24px"
                      color="teal"
                    />
                  ) : (
                    "Reset password"
                  )}
                </Button>
              </form>
            </>
          )}
        </Box>
      </Flex>
    </LayoutView>
  );
};

export default ResetPassword;
